/**
 * Autonomous Agent — plan → approve → execute.
 *
 * Enter a goal. LLM consults the action catalog, proposes a plan of
 * action invocations. User reviews the plan, then runs it. Gated actions
 * pause the run until an admin approves via Actions page.
 */
import { useState, useEffect, useCallback } from 'react'
import {
  Sparkles, Send, Play, CheckCircle2, XCircle, Clock3, AlertCircle,
  Lock, RefreshCw, Bot, ChevronRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface PlanStep {
  step: number
  action_identifier: string
  params: Record<string, any>
  rationale: string
}

interface TranscriptEntry {
  role: string
  content?: string
  step?: number
  action?: string
  ok?: boolean
  detail?: string
  status?: string
  execution_id?: number
  ts: string
}

interface AgentRun {
  id: number
  user_email: string
  goal: string
  plan: PlanStep[]
  transcript: TranscriptEntry[]
  status: string
  created_at: string
  updated_at: string
}

const STATUS_COLOR: Record<string, string> = {
  planning:           'bg-sky/20 text-sky',
  awaiting_approval:  'bg-amber/20 text-amber',
  running:            'bg-sky/20 text-sky',
  completed:          'bg-emerald/20 text-emerald',
  failed:             'bg-rose/20 text-rose',
  cancelled:          'bg-rose/10 text-rose/70',
}

const SAMPLE_GOALS = [
  'Open an access review for techdashboard-api',
  'Scaffold a new Python service owned by platform team',
  'Tag the cto-db-hetzner resource with owner=platform',
  'Ping techdashboard-api health endpoint',
]

export default function AgentRunsPage() {
  const [goal, setGoal] = useState('')
  const [model, setModel] = useState('claude-sonnet-4-6')
  const [runs, setRuns] = useState<AgentRun[]>([])
  const [current, setCurrent] = useState<AgentRun | null>(null)
  const [planning, setPlanning] = useState(false)
  const [executing, setExecuting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await api.get('/api/agent', { params: { limit: 20 } })
      setRuns(data.runs || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Load failed')
    }
  }, [])

  useEffect(() => { loadRuns() }, [loadRuns])

  const plan = async () => {
    if (!goal.trim()) return
    setPlanning(true)
    setError(null)
    try {
      const { data } = await api.post('/api/agent/plan', { goal, model })
      setCurrent(data.run)
      if (data.validation_errors?.length) {
        setError('Validation: ' + data.validation_errors.join('; '))
      }
      await loadRuns()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Planning failed')
    } finally {
      setPlanning(false)
    }
  }

  const execute = async () => {
    if (!current) return
    setExecuting(true)
    setError(null)
    try {
      await api.post(`/api/agent/${current.id}/execute`)
      const { data } = await api.get(`/api/agent/${current.id}`)
      setCurrent(data)
      await loadRuns()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Execute failed')
    } finally {
      setExecuting(false)
    }
  }

  return (
    <div className="min-h-screen bg-graphite text-whitesmoke">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gunmetal">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-3">
              <Bot className="w-6 h-6 text-sky" />
              Autonomous Agent
            </h1>
            <p className="text-sm text-whitesmoke/60 mt-1">
              Describe a goal. The agent picks actions from your catalog and
              runs them — pausing on anything that needs approval.
            </p>
          </div>
          <button
            onClick={loadRuns}
            title="Reload recent runs"
            aria-label="Reload recent runs"
            className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-rose/10 border border-rose/30 text-rose text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              title="Dismiss"
              aria-label="Dismiss"
              className="ml-auto text-rose/60 hover:text-rose text-xs"
            >dismiss</button>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Goal input + current run */}
          <div className="col-span-8 space-y-4">
            <div className="border border-gunmetal rounded p-4 bg-gunmetal/10">
              <div className="text-xs uppercase tracking-wider text-whitesmoke/70 mb-2">New goal</div>
              <textarea
                value={goal}
                onChange={e => setGoal(e.target.value)}
                rows={2}
                placeholder="e.g. Open an access review for techdashboard-api"
                className="w-full px-3 py-2 bg-graphite border border-gunmetal rounded text-sm"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-whitesmoke/60">model:</label>
                  <select
                    aria-label="LLM model"
                    value={model}
                    onChange={e => setModel(e.target.value)}
                    className="px-2 py-1 bg-gunmetal border border-gunmetal rounded text-xs font-mono"
                  >
                    <option value="claude-sonnet-4-6">claude-sonnet-4-6</option>
                    <option value="claude-opus-4-7">claude-opus-4-7</option>
                    <option value="gpt-5">gpt-5</option>
                    <option value="gpt-4o">gpt-4o</option>
                  </select>
                </div>
                <button
                  onClick={plan}
                  disabled={planning || !goal.trim()}
                  className="px-4 py-1.5 rounded bg-sky/20 hover:bg-sky/30 text-sky text-sm flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" /> {planning ? 'Planning…' : 'Plan'}
                </button>
              </div>
              {!goal && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {SAMPLE_GOALS.map(s => (
                    <button
                      key={s}
                      onClick={() => setGoal(s)}
                      className="text-xs px-2 py-1 rounded bg-gunmetal/50 hover:bg-gunmetal/80 text-whitesmoke/70"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {current && (
              <div className="border border-gunmetal rounded">
                <div className="bg-gunmetal/40 px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-whitesmoke/70">Run #{current.id}</div>
                    <div className="text-sm text-whitesmoke/80">{current.goal}</div>
                  </div>
                  <span className={clsx('px-2 py-1 rounded text-xs uppercase', STATUS_COLOR[current.status])}>
                    {current.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="p-4">
                  <div className="text-xs uppercase tracking-wider text-whitesmoke/70 mb-2">
                    Plan ({current.plan.length} steps)
                  </div>
                  {current.plan.length === 0 && (
                    <div className="text-sm text-whitesmoke/50 py-3">
                      Empty plan — LLM couldn't map the goal to any action.
                    </div>
                  )}
                  <ol className="space-y-2">
                    {current.plan.map((s, i) => {
                      const result = current.transcript.find(t => t.role === 'action-result' && t.step === s.step)
                      return (
                        <li key={i} className="flex gap-3 items-start">
                          <span className="w-6 h-6 rounded-full bg-sky/20 text-sky text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                            {s.step}
                          </span>
                          <div className="flex-1">
                            <div className="font-mono text-sm">{s.action_identifier}</div>
                            <div className="text-xs text-whitesmoke/60 mt-0.5">{s.rationale}</div>
                            {Object.keys(s.params || {}).length > 0 && (
                              <div className="text-[10px] text-whitesmoke/50 mt-1 font-mono bg-gunmetal/30 rounded px-2 py-1 inline-block">
                                {Object.entries(s.params).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join('  ')}
                              </div>
                            )}
                            {result && (
                              <div className={clsx(
                                'text-xs mt-2 flex items-center gap-1.5',
                                result.ok ? 'text-emerald' :
                                  result.status === 'pending-approval' ? 'text-amber' : 'text-rose',
                              )}>
                                {result.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                                  result.status === 'pending-approval' ? <Lock className="w-3.5 h-3.5" /> :
                                    <XCircle className="w-3.5 h-3.5" />}
                                {result.status === 'pending-approval' ? (
                                  <span>
                                    Pending admin approval (execution #{result.execution_id}).
                                    See <a href="/app/actions" className="underline">Actions</a>.
                                  </span>
                                ) : (
                                  <span>{result.detail || (result.ok ? 'ok' : 'failed')}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ol>

                  {current.plan.length > 0 && current.status === 'awaiting_approval' && (
                    <div className="mt-4 pt-3 border-t border-gunmetal/50 flex justify-end">
                      <button
                        onClick={execute}
                        disabled={executing}
                        className="px-4 py-1.5 rounded bg-emerald/20 hover:bg-emerald/30 text-emerald text-sm flex items-center gap-2"
                      >
                        <Play className="w-4 h-4" /> {executing ? 'Running…' : 'Execute plan'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Recent runs */}
          <div className="col-span-4">
            <div className="border border-gunmetal rounded overflow-hidden">
              <div className="bg-gunmetal/40 px-3 py-2 text-xs uppercase tracking-wider text-whitesmoke/60">
                Recent runs
              </div>
              {runs.length === 0 && (
                <div className="p-4 text-sm text-whitesmoke/50">No runs yet.</div>
              )}
              <ul className="max-h-[600px] overflow-y-auto">
                {runs.map(r => (
                  <li key={r.id}>
                    <button
                      onClick={() => setCurrent(r)}
                      className={clsx(
                        'w-full text-left px-3 py-2.5 border-b border-gunmetal/50 hover:bg-gunmetal/30',
                        current?.id === r.id && 'bg-sky/10 border-l-2 border-l-sky',
                      )}
                    >
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <span className={clsx('px-1.5 py-0.5 rounded uppercase', STATUS_COLOR[r.status])}>
                          {r.status}
                        </span>
                        <span className="text-whitesmoke/40">#{r.id}</span>
                      </div>
                      <div className="text-sm truncate">{r.goal}</div>
                      <div className="text-xs text-whitesmoke/50 mt-0.5">
                        {r.plan.length} steps · {new Date(r.created_at).toLocaleString()}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
